/**
 * Axios 实例工厂
 * 根据客户端类型（user/cs）创建独立的 axios 实例，各自管理 token 和 401 跳转
 */
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';

import i18n from '@/i18n';
import { type HttpClientConfig, BizError } from './types';
import { getUserToken, clearUserAuth } from '@/utils/store';
import { getCsToken, clearCsAuth } from '@/utils/store';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * i18n 辅助函数（非 React 模块中使用）
 * 绕过 i18next 严格类型检查，支持动态 key
 */
function ti(key: string): string {
  return i18n.t(key as never);
}

// OAuth2 错误码到 i18n key 的映射
const OAUTH2_ERROR_KEYS: Record<string, string> = {
  invalid_client: 'error.invalidClient',
  invalid_grant: 'error.invalidGrant',
  invalid_request: 'error.invalidRequest',
  invalid_scope: 'error.invalidScope',
  unauthorized_client: 'error.unauthorizedClient',
  unsupported_grant_type: 'error.unsupportedGrantType',
  unsupported_response_type: 'error.unsupportedResponseType',
  server_error: 'error.serverInternalError',
  temporarily_unavailable: 'error.serverUnavailable',
};

/**
 * 解析接口错误并返回国际化提示
 * 兼容两种错误格式：
 * 1. OAuth2 标准格式：{ error, error_description }
 * 2. 业务格式：{ code, msg }
 */
function parseErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return ti('error.requestFailed');

  if ('error' in data) {
    const oauth2 = data as { error: string; error_description?: string };
    const i18nKey = OAUTH2_ERROR_KEYS[oauth2.error];
    if (i18nKey) return ti(i18nKey);
    return oauth2.error_description || oauth2.error;
  }

  if ('msg' in data) {
    return (data as { msg: string }).msg || ti('error.requestFailed');
  }

  return ti('error.requestFailed');
}

/**
 * 业务错误中央分发
 * 按后端返回的业务 code 差异化处理，统一 reject 为 BizError
 * 调用方可通过 catch + instanceof BizError + err.code 精确区分错误类型
 *
 * @param code 业务状态码
 * @param msg 后端返回的错误提示
 * @param type 客户端类型（user/cs），用于鉴权相关跳转
 */
function handleBizError(code: string, msg: string, type: 'user' | 'cs'): Promise<never> {
  switch (code) {
    // 业务级 token 过期（与 HTTP 401 互补，部分后端用业务码返回）
    case '4001':
      if (type === 'cs') {
        clearCsAuth();
        window.location.href = '/csLogin';
      } else {
        clearUserAuth();
        window.location.href = '/login';
      }
      return Promise.reject(new BizError(code, ti('error.loginExpired')));

    // 无权限：透传给调用方，由其决定是否弹窗提示
    case '403':
      return Promise.reject(new BizError(code, msg || ti('error.noPermission')));

    // 参数校验失败：透传给调用方，展示表单错误
    case '400':
      return Promise.reject(new BizError(code, msg || ti('error.paramError')));

    // 服务端异常：统一提示
    case '500':
      return Promise.reject(new BizError(code, ti('error.serverError')));

    // 其他未知业务码：原样透传 msg
    default:
      return Promise.reject(new BizError(code, msg || ti('error.requestFailed')));
  }
}

/**
 * 创建 HTTP 客户端实例
 * @param config 客户端配置
 * @returns 配置好的 axios 实例
 */
export function createHttpClient(config: HttpClientConfig): AxiosInstance {
  const { type, baseURL = API_BASE_URL, timeout = 120000 } = config;

  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // ==================== 请求拦截器 ====================
  client.interceptors.request.use(
    (conf) => {
      // 根据类型获取对应的 token
      const token = type === 'cs' ? getCsToken() : getUserToken();
      if (token) {
        const authValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        conf.headers['Authorization'] = authValue;
      }
      // // 将 i18next 语言格式（zh-CN）转换为 Java Locale 格式（zh_CN）
      // const convertToJavaLocale = (lang: string) => {
      //   return lang.replace('-', '_');
      // };
      // // 注入 Accept-Language（从 i18next 获取当前语言，转换为 Java Locale 格式）
      // const currentLang = i18n.language || 'en_US';
      // conf.headers['Accept-Language'] = convertToJavaLocale(currentLang);
        // 注入 Accept-Language（从 i18next 获取当前语言）
        conf.headers['Accept-Language'] = i18n.language || 'en-US';
        return conf;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // ==================== 响应拦截器 ====================
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      const res = response.data;

      // 检测是否为标准 ResData 包装格式（含 code + msg 字段）
      const isResDataFormat =
        res !== null &&
        typeof res === 'object' &&
        !Array.isArray(res) &&
        'code' in res &&
        'msg' in res;

      if (isResDataFormat) {
        const code = String((res as { code: unknown }).code);
        const msg = ((res as { msg?: string }).msg) || ti('error.requestFailed');
        const data = (res as { data: unknown }).data;

        // 业务成功：直接返回内层 data，调用方无需判断 code
        if (code === '200') {
          return data;
        }

        // 业务失败：中央分发，按 code 差异化处理
        return handleBizError(code, msg, type);
      }

      // 非 ResData 格式（如 OAuth2 token 响应），直接透传
      return res;
    },
    (error: AxiosError) => {
      const axiosError = error as AxiosError<unknown>;

      // HTTP 401 Unauthorized - 根据类型处理不同的清理和跳转逻辑
      if (axiosError.response?.status === 401) {
        if (type === 'cs') {
          // 客服端：清理客服 token，跳转客服登录页
          clearCsAuth();
          window.location.href = '/csLogin';
        } else {
          // 用户端：清理用户端 token，跳转用户登录页
          clearUserAuth();
          window.location.href = '/login';
        }
        return Promise.reject(new Error(ti('error.loginExpired')));
      }

      // 服务器返回了错误响应
      if (axiosError.response) {
        const data = axiosError.response.data;
        return Promise.reject(new Error(parseErrorMessage(data)));
      }

      // 网络错误（无响应）
      if (error.request) {
        return Promise.reject(new Error(ti('error.networkError')));
      }

      return Promise.reject(new Error(ti('error.requestFailed')));
    }
  );

  return client;
}
